<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        Value (Identifier) <span class="text-red-500">*</span>
      </label>
      <InputText
        v-model="localDimensionData.value"
        class="w-full"
        placeholder="e.g., leadership, communication"
        :disabled="!!localDimensionData._id"
      />
      <small class="text-gray-500"
        >Unique identifier for the dimension (cannot be changed after creation)</small
      >
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          English Name <span class="text-red-500">*</span>
        </label>
        <InputText
          v-model="localDimensionData.translation.en"
          class="w-full"
          placeholder="e.g., Leadership"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          German Name <span class="text-red-500">*</span>
        </label>
        <InputText
          v-model="localDimensionData.translation.de"
          class="w-full"
          placeholder="e.g., Führung"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          English Description <span class="text-red-500">*</span>
        </label>
        <Textarea
          v-model="localDimensionData.description.en"
          rows="3"
          class="w-full"
          placeholder="Describe what this dimension measures..."
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          German Description <span class="text-red-500">*</span>
        </label>
        <Textarea
          v-model="localDimensionData.description.de"
          rows="3"
          class="w-full"
          placeholder="Beschreiben Sie, was diese Dimension misst..."
        />
      </div>
    </div>

    <!-- Result Descriptions Section -->
    <div class="border-t pt-4">
      <div class="flex justify-between items-center mb-3">
        <h4 class="text-gray-900">Result Descriptions</h4>
        <Button
          label="Add Result Description"
          icon="pi pi-plus"
          class="p-button-sm"
          @click="addResultDescription"
        />
      </div>

      <div
        v-if="
          localDimensionData.resultDescriptions &&
          localDimensionData.resultDescriptions.length > 0
        "
        class="space-y-3"
      >
        <div
          v-for="(resultDesc, index) in localDimensionData.resultDescriptions"
          :key="index"
          class="p-3 border rounded bg-gray-50"
        >
          <div class="flex justify-between items-start mb-2">
            <h5>Result Description {{ index + 1 }}</h5>
            <Button
              icon="pi pi-trash"
              class="p-button-text p-button-danger p-button-sm"
              @click="removeResultDescription(index)"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <Dropdown
                v-model="resultDesc.status"
                :options="statusOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Headline (English)
              </label>
              <InputText
                v-model="resultDesc.headline.en"
                class="w-full"
                placeholder="Enter English headline..."
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Headline (German)
              </label>
              <InputText
                v-model="resultDesc.headline.de"
                class="w-full"
                placeholder="Enter German headline..."
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Content (English)
              </label>
              <Textarea
                v-model="resultDesc.content.en"
                rows="2"
                class="w-full"
                placeholder="Enter content for positive/negative results..."
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Content (German)
              </label>
              <Textarea
                v-model="resultDesc.content.de"
                rows="2"
                class="w-full"
                placeholder="Inhalt für positive/negative Ergebnisse eingeben..."
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Tip (English)</label>
              <Textarea
                v-model="resultDesc.tip.en"
                rows="2"
                class="w-full"
                placeholder="Enter tips and advice..."
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Tip (German)</label>
              <Textarea
                v-model="resultDesc.tip.de"
                rows="2"
                class="w-full"
                placeholder="Tipps und Ratschläge eingeben..."
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-sm text-gray-500 italic">
        No result descriptions added yet. Add at least one to provide feedback for
        assessment results.
      </div>
    </div>

    <!-- Footer Buttons -->
    <div class="flex justify-end space-x-2 pt-4 border-t">
      <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="closeDialog" />
      <Button label="Save" icon="pi pi-check" @click="saveDimensionHandler" :loading="saving" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';

export default {
  name: 'DimensionDialog',
  components: {
    Button,
    InputText,
    Textarea,
    Dropdown,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    dimensionData: {
      type: Object,
      default: () => ({
        value: '',
        translation: {
          en: '',
          de: '',
        },
        description: {
          en: '',
          de: '',
        },
        resultDescriptions: [],
      }),
    },
  },
  emits: ['update:visible'],
  data() {
    return {
      saving: false,
      localDimensionData: {
        value: '',
        translation: {
          en: '',
          de: '',
        },
        description: {
          en: '',
          de: '',
        },
        resultDescriptions: [],
      },
      statusOptions: [
        { label: 'Positive', value: 'positive' },
        { label: 'Negative', value: 'negative' },
      ],
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeData();
      }
    },
    dimensionData: {
      handler(newVal) {
        if (newVal) {
          this.initializeData();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('dimensions', ['saveDimension']),

    initializeData() {
      if (this.dimensionData && this.dimensionData._id) {
        // Editing existing dimension
        this.localDimensionData = {
          _id: this.dimensionData._id,
          value: this.dimensionData.value || '',
          translation: {
            en: this.dimensionData.translation?.en || '',
            de: this.dimensionData.translation?.de || '',
          },
          description: {
            en: this.dimensionData.description?.en || '',
            de: this.dimensionData.description?.de || '',
          },
          resultDescriptions: this.dimensionData.resultDescriptions
            ? [...this.dimensionData.resultDescriptions]
            : [],
        };
      } else {
        // Creating new dimension
        this.localDimensionData = {
          value: '',
          translation: {
            en: '',
            de: '',
          },
          description: {
            en: '',
            de: '',
          },
          resultDescriptions: [],
        };
      }
    },

    addResultDescription() {
      this.localDimensionData.resultDescriptions.push({
        status: 'positive',
        headline: {
          en: '',
          de: '',
        },
        content: {
          en: '',
          de: '',
        },
        tip: {
          en: '',
          de: '',
        },
      });
    },

    removeResultDescription(index) {
      this.localDimensionData.resultDescriptions.splice(index, 1);
    },

    validateDimension() {
      const errors = [];

      if (!this.localDimensionData.value.trim()) {
        errors.push('Dimension value is required');
      }

      if (!this.localDimensionData.translation.en.trim()) {
        errors.push('English translation is required');
      }

      if (!this.localDimensionData.translation.de.trim()) {
        errors.push('German translation is required');
      }

      if (!this.localDimensionData.description.en.trim()) {
        errors.push('English description is required');
      }

      if (!this.localDimensionData.description.de.trim()) {
        errors.push('German description is required');
      }

      // Validate result descriptions
      this.localDimensionData.resultDescriptions.forEach((resultDesc, index) => {
        if (!resultDesc.headline.en.trim()) {
          errors.push(`Result description ${index + 1}: English headline is required`);
        }
        if (!resultDesc.headline.de.trim()) {
          errors.push(`Result description ${index + 1}: German headline is required`);
        }
        if (!resultDesc.content.en.trim()) {
          errors.push(`Result description ${index + 1}: English content is required`);
        }
        if (!resultDesc.content.de.trim()) {
          errors.push(`Result description ${index + 1}: German content is required`);
        }
        if (!resultDesc.tip.en.trim()) {
          errors.push(`Result description ${index + 1}: English tip is required`);
        }
        if (!resultDesc.tip.de.trim()) {
          errors.push(`Result description ${index + 1}: German tip is required`);
        }
      });

      return errors;
    },

    async saveDimensionHandler() {
      console.log('saveDimension', this.localDimensionData);
      const errors = this.validateDimension();

      if (errors.length > 0) {
        this.$toast.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: errors.join(', '),
          life: 5000,
        });
        return;
      }

      this.saving = true;
      try {
        await this.saveDimension(this.localDimensionData);
        this.closeDialog();
      } catch (error) {
        // Error handling is done in the store
      } finally {
        this.saving = false;
      }
    },

    closeDialog() {
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style scoped></style>
